import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLoaderData, useLocation } from 'react-router-dom';
import { Container, Grid, Card, CardContent, Typography, Box, Avatar, TextField, Button, Input, MenuItem, Select, InputLabel, FormHelperText, FormControl, Snackbar, Alert, IconButton, Chip, Skeleton } from '@mui/material';
import { styled } from '@mui/system';
import { Edit, Save, AccountBalance, School, FileUpload, Email, Phone, Info, Language, Home, CalendarToday, LocationOn, Person, Lock, Delete, CheckCircle, Facebook, Instagram, Twitter, LinkedIn, YouTube, X } from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Header1 from '../Header/Header1';
import Header3 from '../Header/Header3';
import Footer from '../Footer/Footer';
import { Link as MuiLink } from '@mui/material';
import GenderIcon from '@mui/icons-material/Male';
import { Link } from 'react-router-dom';
import Footer2 from '../Footer/Footer2';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RecommendationSetup from '../Recommendations/RecommendationSetup';
import avatar from './default-avatar.png'
import { MonetizationOn, PostAdd, Event } from '@mui/icons-material';
import YouTubeEmbed from '../UserProfile/YouTubeEmbed';
import AreasOfInterest from '../UserProfile/AreasOfInterest';
import LanguagesSelection from '../UserProfile/LanguagesSelecetion';
import TimeOfAvailabilitySelection from '../UserProfile/TimeOfAvailabilitySelection';
import VerificationForm from '../UserProfile/VerificationForm';
import VerificationButton from '../UserProfile/VerificationButton';


const API_URL = process.env.REACT_APP_API_URL;




const theme = createTheme({
  palette: {
    primary: {
      main: '#1111',
    },
    secondary: {
      main: '#1111',
    },
    background: {
      paper: '#1111',
    },
  },
});


const ProfileHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #55555, #77777)',
  color: 'white',
  padding: theme.spacing(4),
  borderRadius: '10px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
}));

const ProfileCard = styled(Card)(({ theme }) => ({
  borderRadius: '15px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  color: 'white',
  padding: theme.spacing(4),
  backgroundColor: '#1111',
  marginBottom: theme.spacing(10),
  borderColor: '#5555',
  "&:hover": {
    transform: "translateY(-3px)",
    borderColor: '#1E90FF',
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(40),
  height: theme.spacing(40),
  borderRadius: '50%',
  border: `4px solid ${theme.palette.primary.main}`,
  marginRight: theme.spacing(4),
  borderColor: '#5555',
  "&:hover": {
    transform: "translateY(-3px)",
    borderColor: '#1E90FF',
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.1rem',
  marginBottom: theme.spacing(2),

}));

const InputField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: '100%',
  '& .MuiInputBase-root': {
    backgroundColor: '#2222',
    borderRadius: '10px',
    color: 'white',
  },
  '& .MuiInputBase-input': {
    color: 'white',
  },
  '& .MuiInputLabel-root': {
    color: 'white',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'white',
  }
}));


const DarkForm = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#2222',
  padding: '24px',
  borderRadius: '8px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.4)',
  width: '100%',
  maxWidth: '500px',
  margin: 'auto',
}));

const DarkFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2222',
    color: 'white',
    '& fieldset': {
      borderColor: '#2222',
    },
    '&:hover fieldset': {
      borderColor: '#3333',
    },
  },
}));

const DarkMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: '#2222',
  color: 'white',
  '&:hover': {
    backgroundColor: '#444',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, // Applying border-radius to MenuItem (if applicable)
  },
  '& .MuiInputLabel-root': {
    color: 'white', // Making label color white
  },
}));

// Dark Select with sx styling
const DarkSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    padding: theme.spacing(2),
    backgroundColor: '#2222',
    color: 'white',
    borderRadius: '10px',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, // Applying border-radius to input (for Select)
  },
  '& .MuiInputLabel-root': {
    color: 'white', // Making label color white
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#2222',
  color: 'black',
  padding: theme.spacing(1, 4),
  borderRadius: '25px',
  '&:hover': {
    backgroundColor: '#1E90FF',
  },
  marginTop: theme.spacing(2),
}));


function UserProfileAdminView({ userId, onLogout }) {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);

  const { id } = useParams();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const [language, setLanguage] = useState('en');
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [userStats, setUserStats] = useState({
    totalEarnings: 0,
    postsCount: 0,
    totalBookings: 0,
  });


  const navigate = useNavigate();


  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userData, setUserData] = useState({
    "name": "",
    "about": "",
    "contact": "",
    "address": "",
    "created_at": "",
    "dob": "",
    "email": "",
    "gender": "",
    "id": "",
    "image": "",
    "languages": "",
    "password": "",
    "rating": "",
    "role": "",
    "status": "",
    "surname": "",
    "city": "",
    "state": "",
    "country": "",
    "pincode": "",
    "updated_at": "",
    "facebook": "",
    "instagram": "",
    "x": "",
    "linkedin": "",
    "youtube": "",
    "currentLevel": "Advanced",
    "totalTimeLearning": "150 hrs",
    "totalTimeTeaching": "50 hrs",
    "totalSessions": "25",
    "ratings": "4.8/5",
  });

  const [image, setImage] = useState(null);
  const [error, setError] = useState('');


  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.size > 50 * 1024) {  // 50 KB in bytes
        setError('File size exceeds the 50KB limit.');
        setImage(null);  // Clear the image if it exceeds the limit
      } else {
        setError('');
        setImage(file);
      }
    }
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!image) {
      setError('Please select an image.');
      return;
    }

    const formData = new FormData();
    formData.append('profilePicture', image);
    formData.append('userId', id);

    try {
      const response = await axios.patch(`${API_URL}/update_profile_picture/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Profile picture updated successfully.');
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile picture:', error);
      alert('There was an error updating your profile picture. Please try again later.');
    }
  };





  useEffect(() => {
    if (!id) {
      return;
    }

    axios
      .get(`${API_URL}/get_user_data/?user_id=${id}`)
      .then((response) => {
        setUserData(response.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [id]);


  const extractLanguages = (data) => {
    const allLanguages = [];

    data.forEach(item => {
      allLanguages.push({
        id: item.id,   // Use 'id' as the value
        name: item.name,  // Display the 'name' in the dropdown
      });
    });

    setLanguages(allLanguages);  // Set languages as an array of objects
  };

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await fetch(`${API_URL}/list_sections_and_subsections/`);
        const data = await response.json();
        extractLanguages(data.languages);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchSections();
  }, []);


  useEffect(() => {
    // Fetch total earnings, posts, and bookings for the user
    const fetchUserStats = async () => {
      try {
        // Make the API call
        const response = await axios.get(`${API_URL}/get_user_stats/?user_id=${id}`);

        // Set the response data in the state
        setUserStats({
          totalEarnings: response.data.total_earnings,
          totalPosts: response.data.posts_count,
          totalBookings: response.data.total_bookings_made,
          paybleAmount: response.data.payable_amount
        });
      } catch (err) {
        setError('Error fetching user stats');
      }
    };

    fetchUserStats();
  }, []);


  const handleLanguageChange = (e) => {
    setUserData({
      ...userData,
      language: e.target.value
    });
  };



  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };



  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match.');
      return;
    }

    try {
      const payload = { id, newPassword };

      if (userData.password) {
        payload.currentPassword = currentPassword;
      }

      await axios.patch(`${API_URL}/change_user_password/`, payload);
      alert('Password changed successfully.');
      setShowChangePassword(false);
    } catch (error) {
      console.error('Error changing password:', error);
      alert('There was an error changing your password. Please try again later.');
    }
  };



  const [openSnackbar, setOpenSnackbar] = useState(false);


  const handleChange = (e, name) => {
    setUserData({
      ...userData,
      [name]: e.target.value
    });
  };



  const [selectedSection, setSelectedSection] = useState('');
  const [selectedSubSection, setSelectedSubSection] = useState('');

  const [isEditing, setIsEditing] = useState(false);


  const toggleEditMode = async () => {
    if (isEditing) {
      try {
        const response = await axios.put(`${API_URL}/update_users/${id}/`, userData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}` // Assuming you're using JWT or any token for auth
          }
        });
        alert('Profile updated successfully!');
        window.location.reload();
      } catch (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update profile. Please try again.');
      }
    }
    setIsEditing(!isEditing);
  };


  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        await axios.patch(`${API_URL}/deactivate_user/${id}/`);
        alert('Account deactivated successfully.');
        onLogout();
      } catch (error) {
        console.error('Error deactivating account:', error);
        alert('There was an error deactivating your account. Please try again later.');
      }
    }
  };

  const specializedFields = userData?.specialized_fields || [];
  const roles = userData?.roles || [];



  return (
    <div>
      <Header1 userId={userId} onLogout={onLogout} />

      <Typography
        variant="h3"
        component="h1"
        align="center"
        fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
        sx={{
          backgroundImage: 'linear-gradient(45deg, #ff00ff, #00bfff)',
          mt: 10,
          WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
          color: 'transparent',  // Makes the text itself transparent to show the gradient
          // fontWeight: 'bold',  // Optional: you can make the text bold
        }}
        gutterBottom
      >
        User Portal
      </Typography>
      <Box sx={{ textAlign: 'center', py: 4 }}>

      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          minHeight: '100vh',
          backgroundColor: 'black',
          padding: 2,  // Add padding for spacing
          gap: 2,      // Add space between items in the column
          justifyContent: 'flex-start',
          alignItems: 'center',  // Center items horizontally in smaller screens
          width: '100%',  // Make sure it spans the full width of the screen
          '@media (min-width: 600px)': {
            flexDirection: 'row', // On larger screens, switch to row layout
            justifyContent: 'space-evenly', // Distribute space evenly between items
            alignItems: 'center', // Align items centrally along the row
          },
          '@media (min-width: 1024px)': {
            padding: 4, // Increase padding on larger screens
            gap: 3,     // Increase the gap for a more spacious layout
          },
        }}
      >


        <Grid
          item
          xs={12}
          sm={6}
          md={3}
          lg={2}
          sx={{
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center',  // Centers content horizontally
            alignItems: 'center'       // Centers content vertically
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' }, // Column on small screens, row on medium and up
              flexWrap: 'wrap', // Allow links to wrap if needed
              gap: 2, // Space between links
              backgroundColor: '#1111',
              alignItems: 'center',
              justifyContent: 'center',
              padding: { xs: 1, sm: 2, md: 3 }, // Adjust padding based on screen size
              borderRadius: 2,
              boxShadow: 2,
              borderColor: '#5555',
              "&:hover": {
                transform: "translateY(-3px)",
                borderColor: '#1E90FF',
                boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'center' }, // Center content for small screens
              alignItems: { xs: 'center', sm: 'center' } // Center content vertically for small screens
            }}>



              <MuiLink
                href="#about"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                About
              </MuiLink>

              <MuiLink
                href="#personal-info"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                Personal
              </MuiLink>



              <MuiLink
                href="#social-media-info"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                Social
              </MuiLink>
              <MuiLink
                href="#availability"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                Availability
              </MuiLink>
              <MuiLink
                href="#language-selection"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                Languages
              </MuiLink>
              <MuiLink
                href="#areas-of-interests"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                Interests
              </MuiLink>
              <MuiLink
                href="#Aadhar-and-Pan"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                Aadhar and Pan
              </MuiLink>

              <MuiLink
                href="#address-info"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                Address
              </MuiLink>



              <MuiLink
                href="#change-password"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                Password
              </MuiLink>

              <MuiLink
                href="#delete-account"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  borderRadius: 1,
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: '#2222',
                    color: '#1E90FF',
                  },
                }}
              >
                Delete
              </MuiLink>
            </Box>
          </Box>
        </Grid>



        <ThemeProvider theme={theme}>
          <Container
            maxWidth="lg"
            sx={{
              marginTop: 10,
              mb: 10,
              display: 'flex',
              justifyContent: 'center',  // Centers content horizontally
              alignItems: 'center',      // Centers content vertically
              flexDirection: 'column'    // Ensures that the items are stacked vertically
            }}
          >
            <Grid container spacing={4} justifyContent="center" alignItems={'center'}>


              <Grid
                item
                xs={15}
                sm={10}
                md={8}
                lg={8}    // Custom size for large screens
                xl={10}    // Custom size for extra-large screens
                xxl={10}   // Custom size for even larger screens (if needed)
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>




                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>




                    <Avatar sx={{
                      width: 150,
                      height: 150,
                    }} src={`${API_URL}${userData.image}` || '/default-avatar.png'} alt={userData.name || 'User'} />


                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }}>


                      {userData.linkedin && (
                        <a href={userData.linkedin} target="_blank" rel="noopener noreferrer">
                          <IconButton sx={{
                            color: 'white',
                            marginBottom: { xs: 0.5, sm: 1 }, // Responsive margin bottom
                            '&:hover': { color: '#1E90FF' }
                          }}>
                            <LinkedIn sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }} />
                          </IconButton>
                        </a>
                      )}



                    </Box>



                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mb: 5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mb: 5, mt: 3 }}>
                        <Typography
                          variant="h4"
                          // fontWeight="bold"
                          color="white"
                          sx={{
                            fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem', lg: '2rem' }, // Adjust font size for different screen sizes
                            display: 'flex',  // Use flexbox to arrange the name and surname in a row
                            alignItems: 'center', // Vertically align the text
                          }}
                        >
                          <span>{userData.name}</span>
                          <span style={{ marginLeft: '10px' }}>{userData.surname}</span>
                        </Typography>

                      </Box>

                      {isEditing && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: '#5555',
                            padding: 1,
                            borderRadius: '8px',
                            boxShadow: 3,
                            width: '50%',
                          }}
                        >
                          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              fullWidth
                              sx={{
                                backgroundColor: '#5555',
                                color: 'white',
                                '& input': {
                                  padding: '10px',
                                },
                              }}
                            />
                            <FormHelperText sx={{ color: 'gray' }}>
                              Max file size: 50 KB
                            </FormHelperText>
                            {/* {error && <p style={{ color: 'red' }}>{error}</p>} */}

                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              sx={{
                                width: '100%',
                                marginTop: '16px',
                                backgroundColor: '#0077ff',
                                '&:hover': { backgroundColor: '#005bb5' },
                              }}
                            >
                              Update Profile Picture
                            </Button>
                          </form>
                        </Box>
                      )}


                      <IconButton onClick={toggleEditMode} sx={{ color: 'white', marginTop: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {/* Render the icon */}
                          {isEditing ? <Save sx={{ marginRight: 1 }} /> : <Edit sx={{ marginRight: 1 }} />}

                          {/* Render the text */}
                          <Typography variant="body1">
                            {isEditing ? 'Save' : 'Edit'}
                          </Typography>
                        </Box>
                      </IconButton>
                    </Box>

                  </Box>
                  {/* </Box> */}
                </Box>





                <Box id="preferences" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mb: 5, mt: 3, width: '100%' }}>





                </Box>
                <VerificationButton userId={id} />

                {userData.youtube && (
                  <div>

                    <YouTubeEmbed
                      videoUrl={userData.youtube}
                      title="Sample YouTube Video"
                    />
                  </div>
                )}


                <Box
                  id="about"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 2,
                    boxShadow: 3,  // Add shadow for depth
                    maxWidth: '800px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    textAlign: 'center', // Center the text
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd',// Add a white border
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle sx={{ color: 'white', marginBottom: 2 }}>Current Role</SectionTitle>

                    {/* Conditionally render helper text */}
                    {userData.facebook === null && (
                      <Typography variant="body2" sx={{ color: 'gray', marginBottom: 3 }}>
                        Describe yourself in a few words...
                      </Typography>
                    )}

                    {isEditing ? (
                      <InputField
                        name="aboutMe"
                        value={userData.facebook || ''}
                        onChange={(e) => handleChange(e, 'facebook')}
                        label="Current Role"
                        color="black"
                        backgroundColor="#f0f0f0"
                        variant="outlined"
                        multiline
                        rows={4}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,  // Rounded corners for input field
                          },
                          '& .MuiInputLabel-root': {
                            color: 'white',  // Black label for better contrast
                          },
                          '& .MuiInputBase-root': {
                            padding: '12px 14px',  // Add padding for spacious input
                          },
                        }}
                      />
                    ) : (
                      <Typography variant="body1" color="white" sx={{ marginTop: 2 }}>
                        {userData.facebook || 'No description available'}
                      </Typography>
                    )}
                  </Box>
                </Box>


                {/* About you Card */}
                <Box
                  id="about"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 2,
                    boxShadow: 3,  // Add shadow for depth
                    maxWidth: '800px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    textAlign: 'center', // Center the text
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd',// Add a white border
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle sx={{ color: 'white', marginBottom: 2 }}>About you</SectionTitle>

                    {/* Conditionally render helper text */}
                    {userData.about === null && (
                      <Typography variant="body2" sx={{ color: 'gray', marginBottom: 3 }}>
                        Describe yourself in a few words...
                      </Typography>
                    )}

                    {isEditing ? (
                      <InputField
                        name="aboutMe"
                        value={userData.about || ''}
                        onChange={(e) => handleChange(e, 'about')}
                        label="About Me"
                        color="black"
                        backgroundColor="#f0f0f0"
                        variant="outlined"
                        multiline
                        rows={4}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,  // Rounded corners for input field
                          },
                          '& .MuiInputLabel-root': {
                            color: 'white',  // Black label for better contrast
                          },
                          '& .MuiInputBase-root': {
                            padding: '12px 14px',  // Add padding for spacious input
                          },
                        }}
                      />
                    ) : (
                      <Typography variant="body1" color="white" sx={{ marginTop: 2 }}>
                        {userData.about || 'No description available'}
                      </Typography>
                    )}
                  </Box>
                </Box>


                {/* Experience and Background Card */}
                <Box
                  id="about"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 2,
                    boxShadow: 3,  // Add shadow for depth
                    maxWidth: '800px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    textAlign: 'center', // Center the text
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd',// Add a white border
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle sx={{ color: 'white', marginBottom: 2 }}>Experience and Background</SectionTitle>

                    {/* Conditionally render helper text */}
                    {userData.instagram === null && (
                      <Typography variant="body2" sx={{ color: 'gray', marginBottom: 3 }}>
                        Describe your experience and background in a few words...
                      </Typography>
                    )}

                    {isEditing ? (
                      <InputField
                        name="ExperienceAndBackground"
                        value={userData.instagram || ''}
                        onChange={(e) => handleChange(e, 'instagram')}
                        label="Experience and Background"
                        color="black"
                        backgroundColor="#f0f0f0"
                        variant="outlined"
                        multiline
                        rows={4}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,  // Rounded corners for input field
                          },
                          '& .MuiInputLabel-root': {
                            color: 'white',  // Black label for better contrast
                          },
                          '& .MuiInputBase-root': {
                            padding: '12px 14px',  // Add padding for spacious input
                          },
                        }}
                      />
                    ) : (
                      <Typography variant="body1" color="white" sx={{ marginTop: 2 }}>
                        {userData.instagram || 'No description available'}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Passion and Interests Card */}
                <Box
                  id="x"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 2,
                    boxShadow: 3,  // Add shadow for depth
                    maxWidth: '800px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    textAlign: 'center', // Center the text
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd',// Add a white border
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle sx={{ color: 'white', marginBottom: 2 }}>Passion and Interests</SectionTitle>

                    {/* Conditionally render helper text */}
                    {userData.x === null && (
                      <Typography variant="body2" sx={{ color: 'gray', marginBottom: 3 }}>
                        Describe your passion and interests in a few words...
                      </Typography>
                    )}

                    {isEditing ? (
                      <InputField
                        name="PassionAndInterests"
                        value={userData.x || ''}
                        onChange={(e) => handleChange(e, 'x')}
                        label="Passion and Interests"
                        color="black"
                        backgroundColor="#f0f0f0"
                        variant="outlined"
                        multiline
                        rows={4}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,  // Rounded corners for input field
                          },
                          '& .MuiInputLabel-root': {
                            color: 'white',  // Black label for better contrast
                          },
                          '& .MuiInputBase-root': {
                            padding: '12px 14px',  // Add padding for spacious input
                          },
                        }}
                      />
                    ) : (
                      <Typography variant="body1" color="white" sx={{ marginTop: 2 }}>
                        {userData.x || 'No description available'}
                      </Typography>
                    )}
                  </Box>
                </Box>


                {/* Personal Information Card */}
                <Box
                  id="personal-info"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 3,
                    boxShadow: 5,  // Add shadow for depth
                    maxWidth: '900px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    textAlign: 'center', // Center the text
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd', // Add border (white in this case)
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle sx={{ color: 'white', marginBottom: 3 }}>
                      {/* <Person sx={{ fontSize: '2rem', marginRight: 2 }} />  */}
                      Personal Info
                    </SectionTitle>

                    <Grid container spacing={3}>
                      {/* First Name */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" color="white">
                            First Name: {userData.name}
                          </Typography>
                        ) : (
                          <InputField
                            name="name"
                            value={userData.name}
                            onChange={(e) => handleChange(e, 'name')}
                            label="First Name"
                            variant="outlined"
                            fullWidth
                            color="black"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2, // Rounded corners for input field
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',  // Black label for better contrast
                              },
                              '& .MuiInputBase-root': {
                                padding: '14px',  // Add padding for spacious input
                              },
                            }}
                          />
                        )}
                      </Grid>

                      {/* Last Name */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" color="white">
                            Last Name: {userData.surname}
                          </Typography>
                        ) : (
                          <InputField
                            name="surname"
                            value={userData.surname}
                            onChange={(e) => handleChange(e, 'surname')}
                            label="Last Name"
                            variant="outlined"
                            fullWidth
                            color="black"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',
                              },
                            }}
                          />
                        )}
                      </Grid>

                      {/* Date of Birth */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" color="white">
                            Date of Birth: {userData.dob}
                          </Typography>
                        ) : (
                          <InputField
                            name="dob"
                            value={userData.dob}
                            onChange={(e) => handleChange(e, 'dob')}
                            label="Date of Birth"
                            variant="outlined"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',
                              },
                            }}
                          />
                        )}
                      </Grid>

                      {/* Gender */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1" color="white">
                            Gender: {userData.gender}
                          </Typography>
                        ) : (
                          <DarkSelect
                            name="gender"
                            value={userData.gender}
                            onChange={(e) => handleChange(e, 'gender')}
                            label="Gender"
                            variant="outlined"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white', // Ensure label text is white
                              },
                              '& .MuiSelect-select': {
                                color: 'white', // Ensures text inside the select box is white
                              },
                            }}
                          >
                            <DarkMenuItem value="Male">Male</DarkMenuItem>
                            <DarkMenuItem value="Female">Female</DarkMenuItem>
                            <DarkMenuItem value="Other">Other</DarkMenuItem>
                          </DarkSelect>
                        )}
                      </Grid>

                    </Grid>
                  </Box>
                </Box>




                <Box

                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: '#000000',  // Dark background for modern look
                    borderRadius: 3,
                    boxShadow: 5,  // Add shadow for depth
                    maxWidth: '900px', // Limit the width for large screens
                    margin: '0 auto',  // Center on the page
                    marginTop: 10,  // Add top margin for space
                    width: '100%',  // Make it responsive
                    border: '1px solid #ddd',  // Add border (white in this case)
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',  // Ensure the content is stacked vertically
                      alignItems: 'center',  // Center-align the content horizontally
                    }}
                  >
                    <SectionTitle sx={{ color: 'white', marginBottom: 3 }}>Contact Info</SectionTitle>

                    <Grid container spacing={2} justifyContent="center" alignItems="center">
                      <Grid item xs={12} sm={6}>
                        {!isEditing && (
                          <Typography variant="body1" color="white">
                            <Email sx={{ fontSize: '1.2rem', marginRight: 1 }} /> Email: {userData.email}
                          </Typography>
                        )}
                        {isEditing && (
                          <InputField
                            name="email"
                            value={userData.email}
                            onChange={(e) => handleChange(e, 'email')}
                            label="Email"
                            variant="outlined"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        {!isEditing && (
                          <Typography variant="body1" color="white">
                            <Phone sx={{ fontSize: '1.2rem', marginRight: 1 }} /> Contact: {userData.contact}
                          </Typography>
                        )}
                        {isEditing && (
                          <InputField
                            name="contact"
                            value={userData.contact}
                            onChange={(e) => handleChange(e, 'contact')}
                            label="Phone Number"
                            variant="outlined"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputLabel-root': {
                                color: 'white',
                              },
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                <Box
                  id="social-media-info"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    mt: 10,
                    borderRadius: 2,
                    maxWidth: '900px',
                    border: '1px solid #ddd',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle
                      sx={{
                        textAlign: 'center',
                        marginBottom: 3,
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#ffff',
                      }}
                    >
                      LinkedIn Profile
                    </SectionTitle>
                    <Grid container spacing={2} sx={{ justifyContent: 'center' }}>


                      {/* LinkedIn */}
                      <Grid item xs={12} sm={6} md={4}>
                        {!isEditing ? (
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: {
                                xs: '0.875rem',
                                sm: '1rem',
                              },
                              display: 'flex',
                              alignItems: 'center',
                              marginRight: {
                                xs: 0.5,
                                sm: 1,
                              },
                              padding: '0.5rem 0',
                            }}
                          >
                            <LinkedIn sx={{ marginRight: 1 }} /> LinkedIn: {userData.linkedin}
                          </Typography>
                        ) : (
                          <InputField
                            name="linkedin"
                            value={userData.linkedin}
                            onChange={(e) => handleChange(e, 'linkedin')}
                            label="LinkedIn URL"
                            variant="outlined"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                fontSize: '1rem',
                              },
                              padding: '0.5rem',
                            }}
                          />
                        )}
                      </Grid>


                    </Grid>
                  </Box>
                </Box>


                {/* Social Media Profiles Info Card */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    mt: 10,
                    borderRadius: 2,
                    maxWidth: '900px',
                    border: '1px solid #ddd',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle
                      sx={{
                        textAlign: 'center',
                        marginBottom: 3,
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#ffff',
                      }}
                    >
                      Youtube Demo
                    </SectionTitle>
                    <Grid container spacing={2} sx={{ justifyContent: 'center' }}>


                      {/* YouTube */}
                      <Grid item xs={12} sm={6} md={4}>
                        {!isEditing ? (
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: {
                                xs: '0.875rem',
                                sm: '1rem',
                              },
                              display: 'flex',
                              alignItems: 'center',
                              marginRight: {
                                xs: 0.5,
                                sm: 1,
                              },
                              padding: '0.5rem 0',
                            }}
                          >
                            <YouTube sx={{ marginRight: 1 }} /> YouTube: {userData.youtube}
                          </Typography>
                        ) : (
                          <InputField
                            name="youtube"
                            value={userData.youtube}
                            onChange={(e) => handleChange(e, 'youtube')}
                            label="YouTube URL"
                            variant="outlined"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                fontSize: '1rem',
                              },
                              padding: '0.5rem',
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                <Box id="availability" > <TimeOfAvailabilitySelection userId={id} /></Box>

                <Box id="language-selection" ><LanguagesSelection userId={id} /></Box>

                <Box id="areas-of-interests" ><AreasOfInterest userId={id} /></Box>

                <Box id="Aadhar-and-Pan"  > <VerificationForm userId={id} /></Box>







                {/* Address Information Card */}
                <Box
                  id="address-info"
                  sx={{
                    display: 'flex',

                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    borderRadius: 2,
                    mt: 10,
                    maxWidth: '900px',
                    // margin: 'auto',
                    border: '1px solid #ddd',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 6
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle
                      sx={{
                        textAlign: 'center',
                        marginBottom: 3,
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#ffff'
                      }}
                    >
                      <LocationOn sx={{ marginRight: 1 }} /> Address Info
                    </SectionTitle>

                    <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
                      {/* Address Line Field */}
                      <Grid item xs={12}>
                        {!isEditing ? (
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'gray',  // Default color
                              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' }, // Adjust font size for responsiveness
                              lineHeight: { xs: 1.4, sm: 1.6, md: 1.8 }, // Adjust line height for better readability
                              wordBreak: 'break-word', // Ensures long words break correctly on smaller screens
                            }}
                          >
                            Address Line: {userData.address || <span style={{ color: 'gray' }}>Not available</span>}
                          </Typography>

                        ) : (
                          <>
                            <InputField
                              name="address"
                              value={userData.address}
                              onChange={(e) => handleChange(e, 'address')}
                              label="Address Line"
                              variant="outlined"
                              fullWidth
                              inputProps={{ maxLength: 100 }} // Set max length to 100 characters
                            />
                            <Typography variant="body2" color="gray" sx={{ marginTop: 1 }}>
                              (Max 100 characters)
                            </Typography>
                          </>
                        )}
                      </Grid>

                      {/* City Field */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1">
                            City: {userData.city || <span style={{ color: 'gray' }}>Not available</span>}
                          </Typography>
                        ) : (
                          <InputField
                            name="city"
                            value={userData.city}
                            onChange={(e) => handleChange(e, 'city')}
                            label="City"
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      </Grid>

                      {/* State Field */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1">
                            State: {userData.state || <span style={{ color: 'gray' }}>Not available</span>}
                          </Typography>
                        ) : (
                          <InputField
                            name="state"
                            value={userData.state}
                            onChange={(e) => handleChange(e, 'state')}
                            label="State"
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      </Grid>

                      {/* Country Field */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1">
                            Country: {userData.country || <span style={{ color: 'gray' }}>Not available</span>}
                          </Typography>
                        ) : (
                          <InputField
                            name="country"
                            value={userData.country}
                            onChange={(e) => handleChange(e, 'country')}
                            label="Country"
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      </Grid>

                      {/* Pincode Field */}
                      <Grid item xs={12} sm={6}>
                        {!isEditing ? (
                          <Typography variant="body1">
                            Pincode: {userData.pincode || <span style={{ color: 'gray' }}>Not available</span>}
                          </Typography>
                        ) : (
                          <InputField
                            name="pincode"
                            value={userData.pincode}
                            onChange={(e) => handleChange(e, 'pincode')}
                            label="Pincode"
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Box>



                <Box
                  id="change-password"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    borderRadius: 2,

                    maxWidth: '600px',
                    margin: 'auto',
                    border: '1px solid #ddd',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 6
                    },

                    mt: 10,  // Add margin-top (adjust the value as needed)
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle
                      sx={{
                        textAlign: 'center',
                        marginBottom: 3,
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#ffff'
                      }}
                    >
                      Change Password
                    </SectionTitle>

                    {/* Current Password Field (only if user has password) */}
                    {userData.password ? (
                      <InputField
                        label="Current Password"
                        type="password"
                        variant="outlined"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body2" color="gray" sx={{ textAlign: 'center', marginTop: 2 }}>
                        Current password is not available.
                      </Typography>
                    )}

                    {/* New Password Field */}
                    <InputField
                      label="New Password"
                      type="password"
                      variant="outlined"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      fullWidth
                    />

                    {/* Confirm New Password Field */}
                    <InputField
                      label="Confirm New Password"
                      type="password"
                      variant="outlined"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      fullWidth
                    />

                    {/* Change Password Button */}
                    <Button
                      onClick={handleChangePassword}
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{
                        marginTop: 3,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: 1.5,
                      }}
                    >
                      Change Password
                    </Button>
                  </Box>
                </Box>


                {/* Delete Account Card */}
                <Box
                  id="delete-account"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 4,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    borderRadius: 2,
                    maxWidth: '600px',
                    margin: 'auto',
                    mt: 10,
                    border: '1px solid #ddd',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 6
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <SectionTitle
                      sx={{
                        textAlign: 'center',
                        marginBottom: 2,
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: 'white'
                      }}
                    >
                      Delete Account
                    </SectionTitle>

                    <Typography variant="body2" color="gray" sx={{ textAlign: 'center', marginBottom: 3 }}>
                      Deleting your account is permanent and cannot be undone.
                    </Typography>

                    {/* Display helper text if data is unavailable */}
                    {userData?.name ? (
                      <Typography variant="body2" color="#1E90FF" sx={{ textAlign: 'center', marginBottom: 3 }}>
                        Your account is currently active.
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="gray" sx={{ textAlign: 'center', marginBottom: 3 }}>
                        Account status is not available.
                      </Typography>
                    )}

                    <Button
                      onClick={handleDeleteAccount}
                      variant="contained"
                      color="error"
                      fullWidth
                      sx={{
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: 1.5,
                        marginTop: 2,
                        '&:hover': {
                          backgroundColor: '#d32f2f',
                        }
                      }}
                    >
                      Delete Account
                    </Button>
                  </Box>
                </Box>

              </Grid>
            </Grid>
          </Container>
        </ThemeProvider>

        {/* Snackbar for feedback messages */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert severity="success" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
      <IconButton
        onClick={scrollToTop}
        sx={{ position: 'fixed', bottom: 16, right: 16, backgroundColor: 'white', color: 'black' }}>
        <ArrowUpwardIcon />
      </IconButton>
      <Footer2 />
    </div>

  );
}

export default UserProfileAdminView;